import React, { useEffect, useState } from "react";
import { consultantService } from "../../services/consultant.service";
import { Table, Select, message, Tag } from "antd";
import moment from "moment";
import "moment/locale/de";
import {callAPI, USER_TYPE} from "../../services/api-service"; // Set moment to German

moment.locale("de");

const { Option } = Select;

export default function ConsultantVerificationMismatch() {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [status, setStatus] = useState(""); // Default: Show all statuses

	useEffect(() => {
		fetchMismatches();
	}, [page, limit, status]);

	const fetchMismatches = async () => {
		try {
			setLoading(true);
			const result = await new consultantService().getMismatches({ page, limit, status });
			if (result.success) {
				setData(result.data.data);
				setTotal(result.data.total);
			} else {
				message.error("Fehler beim Laden der Daten.");
			}
		} catch (error) {
			message.error("Ein Fehler ist aufgetreten.");
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = async (userId, newStatus) => {
		try {
			setLoading(true);
			const result = await new consultantService().changeMismatchStatus(userId, newStatus);
			if (result.success) {
				message.success("Status erfolgreich aktualisiert!");
				fetchMismatches(); // Refresh list but keep the same page
			} else {
				message.error("Status konnte nicht aktualisiert werden.");
			}
		} catch (error) {
			message.error("Ein Fehler ist aufgetreten.");
		} finally {
			setLoading(false);
		}
	};

	// Define German translations & colors for statuses
	const statusColors = {
		APPROVED: { color: "green", label: "Genehmigt" },
		REJECTED: { color: "red", label: "Abgelehnt" },
		IN_REVIEW: { color: "gold", label: "In Überprüfung" },
	};

	async function downloadKyc(user_id) {
		const result = await new consultantService().getVeriffImagesByConsultant(user_id);
		const url = window.URL.createObjectURL(new Blob([result.data]));
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "verifizierungs-datein.zip");
		document.body.appendChild(link);
		link.click();
	}

	const columns = [
		{
			title: "Benutzer-ID",
			dataIndex: "user_id",
			key: "user_id",
		},
		{
			title: "Daten Finanzamt",
			dataIndex: "taxofficedata",
			key: "taxofficedata",
			render: (text) => text || "—",
		},
		{
			title: "Daten KYC",
			dataIndex: "kycdata",
			key: "kycdata",
			render: (text) => text || "—",
		},
		{
			title: "Ändere Status",
			key: "change_status",
			render: (record) => (
				<Select
					value={statusColors[record.status]?.label} // ✅ Show German text instead of English
					onChange={(newStatus) => handleStatusChange(record.user_id, newStatus)}
					style={{ width: 160 }}
				>
					{record.status !== "APPROVED" && <Option value="APPROVED">✅ Genehmigt</Option>}
					{record.status !== "REJECTED" && <Option value="REJECTED">❌ Abgelehnt</Option>}
					{record.status !== "IN_REVIEW" && <Option value="IN_REVIEW">🟡 In Überprüfung</Option>}
				</Select>
			),
		},
		{
			title: "Erstellt am",
			dataIndex: "created_at",
			key: "created_at",
			render: (text) => (text ? moment(text).format("DD.MM.YYYY HH:mm") : "—"),
		},
		{
			title: "Zuletzt aktualisiert",
			dataIndex: "updated_at",
			key: "updated_at",
			render: (text) =>
				text ? (
					<Tag color="blue" style={{ fontWeight: "bold" }}>{moment(text).format("DD.MM.YYYY HH:mm")}</Tag>
				) : (
					"—"
				),
		},
		{
			title: "Date herunterladen",
			dataIndex: "user_id",
			key: "download",
			render: (record) =>
				<i onClick={() => downloadKyc(record)} style={{cursor: "pointer"}} className="fas fa-download download_icon"></i>
		},
	];


	return (
		<div style={{padding: "20px" }}>
			<h1>Überprüfung - Abweichungen</h1>

			{/* Status Filter */}
			<Select
				value={status}
				onChange={(value) => setStatus(value)}
				style={{ width: 220, marginBottom: 20 }}
				placeholder="🔍 Nach Status filtern"
				allowClear
			>
				<Option value="">Alle</Option>
				<Option value="APPROVED">✅ Genehmigt</Option>
				<Option value="REJECTED">❌ Abgelehnt</Option>
				<Option value="IN_REVIEW">🟡 In Überprüfung</Option>
			</Select>

			{/* Table */}
			<Table
				dataSource={data}
				columns={columns}
				rowKey="user_id"
				loading={loading}
				pagination={{
					current: page,
					pageSize: limit,
					total: total,
					showSizeChanger: true,
					onChange: (newPage, newPageSize) => {
						setPage(newPage);
						setLimit(newPageSize);
					},
				}}
			/>
		</div>
	);
}
